import {createApi} from '@reduxjs/toolkit/query/react';

import {AxiosRequestHeaders} from 'axios';

import httpBaseQuery from 'shared/api/rtk-query/httpBaseQuery';
import {ApiUrls} from 'shared/config/model/urls';

import {buildQuery} from 'shared/api/query';
import {providesList} from 'shared/api/rtk-query/RTKQueryHelper';

import IPagination from 'shared/api/model/IPagination';
import IHydraResponse from 'shared/api/model/HydraResponse';
import {paginationAdapter} from 'shared/api/paginationHelper';
import ICollectionParams from 'shared/api/model/ICollectionParams';

import IPayment, {ILegalInvoice, IPaymentCreate, IPaymentManualCreate, IPaymentUserStats} from '../model/IPayment';

export const paymentAPI = createApi({
    reducerPath: 'paymentAPI',
    baseQuery: httpBaseQuery(),
    tagTypes: ['Payment'],
    refetchOnMountOrArgChange: 60,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    endpoints: (build) => ({
        addPayment: build.mutation<IPayment, IPaymentCreate>({
            query: (data) => ({
                path: ApiUrls.PAYMENTS(),
                method: 'POST',
                data,
            }),
            invalidatesTags: [{ type: 'Payment', id: 'LIST' }],
        }),
        addManualPayment: build.mutation<IPayment, IPaymentManualCreate>({
            query: (data) => ({
                path: ApiUrls.PAYMENTS_MANUAL,
                method: 'POST',
                data,
            }),
            invalidatesTags: [{ type: 'Payment', id: 'LIST' }],
        }),
        getPayments: build.query<IPagination<IPayment>, ICollectionParams>({
            query: ({page, sort, filters, all = false}) => ({
                path: buildQuery(ApiUrls.PAYMENTS(page, sort, all, filters)),
                headers: {Accept: 'application/ld+json'} as unknown as AxiosRequestHeaders,
            }),
            providesTags: (result) => providesList(result?.items, 'Payment'),
            transformResponse(
                rawResult: IHydraResponse<IPayment>,
            ): Promise<IPagination<IPayment>> | IPagination<IPayment> {
                return paginationAdapter(rawResult);
            },
        }),
        getPayment: build.query<IPayment, number>({
            query: (id) => ({
                path: ApiUrls.PAYMENT(id),
            }),
            providesTags: (result, error, id) => [{ type: 'Payment', id }],
        }),
        updatePayment: build.mutation<IPayment, Pick<IPayment, 'id' | 'status'>>({
            query: ({id, ...data}) => ({
                path: ApiUrls.PAYMENT(id),
                method: 'PUT',
                data,
            }),
            async onQueryStarted({ id, ...patch }, { dispatch }) {
                dispatch(
                    paymentAPI.util.updateQueryData('getPayment', id, (draft) => {
                        Object.assign(draft, patch);
                    }),
                );
            },
            invalidatesTags: [{ type: 'Payment', id: 'LIST' }],
        }),
        getPaymentUserStats: build.query<IPaymentUserStats, void>({
            query: () => ({
                path: ApiUrls.PAYMENT_USER_STATS(),
            }),
        }),
        addLegalInvoice: build.mutation<{success: boolean}, ILegalInvoice>({
            query: (data) => ({
                path: ApiUrls.LEGAL_INVOICE,
                method: 'POST',
                data,
            }),
        }),
    }),
});